import { useEffect, useReducer, useRef } from 'react';

import { io } from 'socket.io-client';

import { getUrls } from '../../../utils/url';
import { AnyAction, ElementActions, PageActions, WhiteboardSessionActions } from '../state/WhiteboardSessionState';
import { UserInfo } from '../types';
import { log } from '../util/misc';

type SocketInfo = {
    auth: { sessionId: string };
    sessionId: string;
    userId: string;
    socket: any;
};

export type SocketState = {
    socket: SocketInfo | null;
    connectionStatus: 'connecting' | 'connected' | 'closed';
    error: SocketInfo | null;
};

const initialState = {
    socket: null,
    connectionStatus: 'connecting',
    error: null
};

/**
 * Connectivity state.
 * @param state
 * @param action
 */
function socketStateReducer(state, action): SocketState {
    switch (action.type) {
        case 'connection-established':
            return {
                socket: action.payload,
                connectionStatus: 'connected',
                error: null
            };
        case 'connection-failed':
            return {
                socket: null,
                connectionStatus: 'closed',
                error: action.payload
            };
        case 'connection-closed':
            return {
                socket: null,
                connectionStatus: 'closed',
                error: null
            };
        case 'reconnect':
            return initialState as SocketState;
        default:
            throw new Error('Invalid socket state action.');
    }
}

/**
 * Connect to websocket server. Listen for events in joined channels and update state.
 *
 * Events are scoped to channels that the socket session has subscribed to:
 * @see {@link WhiteboardContextInterface.joinWhiteboardChannel} For joining channels.
 * @see {@link WhiteboardContextInterface.leaveWhiteboardChannel} For leaving channels.
 *
 * @param {string | undefined} whiteboardSessionId
 * @param {UserInfo | undefined} userInfo
 * @param {React.Dispatch<PageAction>} dispatchPageAction
 * @param {React.Dispatch<ElementAction>} dispatchRemoteElementAction
 * @param {React.Dispatch<WhiteboardSessionAction>} dispatchWhiteboardSessionAction
 * @param {(element: any) => void} setMetadataElement
 */
export const useWebsocket = (
    whiteboardSessionId: string | undefined,
    userInfo: UserInfo | undefined,
    dispatchWhiteboardSessionAction: React.Dispatch<AnyAction>
) => {
    const [socketState, dispatchSocketAction] = useReducer(socketStateReducer, initialState as SocketState);
    const socketStateRef = useRef(socketState);

    socketStateRef.current = socketState;
    const { websocket: socketUrl } = getUrls();

    useEffect(() => {
        if (!whiteboardSessionId || !userInfo) {
            return;
        }

        // Store socket session ID uniquely by whiteboard session.
        // Otherwise this cache property will conflict between whiteboard sessions.
        const uniqueCacheKey = `${whiteboardSessionId}_socket_session`;
        const sessionId = localStorage.getItem(uniqueCacheKey);

        const socket = io(socketUrl, {
            withCredentials: true
        });

        if (sessionId) {
            // Reconnect with previous socket session.
            socket.auth = { sessionId };
            socket.connect();
        }

        // Socket session events (NOT whiteboard session events).
        socket.on('session', (data: any) => {
            log('socket session', data);
            localStorage.setItem(uniqueCacheKey, data.sessionId);
            dispatchSocketAction({
                type: 'connection-established',
                payload: {
                    auth: { sessionId: data.sessionId },
                    sessionId: data.sessionId,
                    userId: data.userId,
                    socket
                }
            });
        });

        // Listen for events relating to state (whiteboard session, pages, elements, and metadata).
        // This is where all remote changes are dispatched to update local state.
        socket.onAny((event, data) => {
            log(`WS ${event}`, data);
            if (
                (data.presenter && data.presenter.student_id === userInfo.student_id) ||
                (data.metadata && data.metadata.lastUpdatedBy === userInfo.student_id)
            ) {
                // Skip any messages originating locally.
                log('skipping websocket message');
                return;
            }

            switch (event) {
                case WhiteboardSessionActions.Updated:
                case WhiteboardSessionActions.Deleted:
                    if (data.session_id !== whiteboardSessionId) break;
                    dispatchWhiteboardSessionAction({ type: event, payload: { session: data } });
                    break;
                case WhiteboardSessionActions.Present:
                    dispatchWhiteboardSessionAction({ type: event, payload: { presenter: data.presenter } });
                    break;
                case PageActions.Created:
                case PageActions.Updated:
                    dispatchWhiteboardSessionAction({ type: event, payload: { pages: data.pages } });
                    break;
                case PageActions.Deleted:
                    dispatchWhiteboardSessionAction({ type: event, payload: { page_ids: data.page_ids } });
                    break;
                case ElementActions.RemoteCreated:
                case ElementActions.RemoteUpdated:
                    dispatchWhiteboardSessionAction({ type: event, payload: { element: data } });
                    break;
                case ElementActions.RemoteDeleted:
                    dispatchWhiteboardSessionAction({ type: event, payload: data });
                    break;
                case 'generic':
                    if (data?.func === 'update_presenter_position') {
                        dispatchWhiteboardSessionAction({
                            type: WhiteboardSessionActions.PresenterPosition,
                            payload: data.data
                        });
                    } else if (data?.func === 'update_element') {
                        dispatchWhiteboardSessionAction({ type: ElementActions.RemoteUpdated, payload: data.data });
                    }
                    break;
                default:
                    break;
            }
        });

        socket.on('disconnect', (reason) => {
            // eslint-disable-next-line no-console
            console.log('Socket disconnected: ', reason);
            dispatchSocketAction({ type: 'connection-closed' });
        });

        socket.io.on('reconnect_attempt', () => {
            // eslint-disable-next-line no-console
            console.log('Socket attempting to reconnect...');
            dispatchSocketAction({ type: 'reconnect' });
        });

        socket.io.on('reconnect', () => {
            // eslint-disable-next-line no-console
            console.log('Socket reconnected.');
            dispatchSocketAction({
                type: 'connection-established',
                payload: { ...socketStateRef.current.socket, socket }
            });
        });

        socket.on('connect_error', () => {
            // eslint-disable-next-line no-console
            console.log('Socket connection error!');
            dispatchSocketAction({ type: 'connection-failed', payload: new Error('A connection error occurred.') });
        });

        return () => {
            socket.close();
        };
    }, [whiteboardSessionId, userInfo, socketUrl, dispatchWhiteboardSessionAction]);

    const pushMessage = (data) => {
        if (socketStateRef.current.socket?.socket) {
            socketStateRef.current.socket.socket.emit('message', data);
        }
    };

    return { socketState, pushMessage };
};
